import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { NAV } from '../../constants/testIds';

const LINKS = [
  { label: 'Why Aravi', to: '/philosophy#namesake', tid: NAV.linkHome },
  { label: 'Residences', to: '/residences', tid: NAV.linkResidences },
  { label: 'Philosophy', to: '/philosophy', tid: NAV.linkPhilosophy },
  { label: 'Landscapes', to: '/landscapes', tid: NAV.linkLandscapes },
  { label: 'Sustainability', to: '/sustainability', tid: NAV.linkSustainability },
  { label: 'Contact', to: '/contact', tid: NAV.linkContact },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // On non-home pages, always use the "scrolled" (light) style
  const condensed = !isHome || scrolled;

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        condensed
          ? 'bg-[#F5F1E8]/85 backdrop-blur-xl border-b border-[#C9A458]/25 py-3'
          : 'bg-transparent border-b border-transparent py-5'
      }`}
    >
      <div className="px-6 md:px-10 lg:px-16 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex flex-col leading-none select-none"
        >
          <span
            className={`font-serif tracking-[0.25em] text-2xl md:text-[28px] transition-colors duration-500 ${
              condensed ? 'text-[#18342A]' : 'text-[#F5F1E8]'
            }`}
            style={{ fontWeight: 500 }}
          >
            ARAVI
          </span>
          <span
            className={`text-[9px] md:text-[10px] tracking-[0.45em] mt-1 transition-colors duration-500 ${
              condensed ? 'text-[#6D6A61]' : 'text-[#C9A458]'
            }`}
          >
            ECO HOMES
          </span>
        </Link>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => {
            const active = pathname === l.to || (l.to !== '/' && pathname.startsWith(l.to));
            return (
              <Link
                key={l.label}
                to={l.to}
                data-testid={l.tid}
                className={`link-underline text-[11px] tracking-[0.28em] uppercase transition-colors duration-500 ${
                  condensed
                    ? `${active ? 'text-[#A66B45]' : 'text-[#18342A]'} hover:text-[#A66B45]`
                    : `${active ? 'text-[#C9A458]' : 'text-[#F5F1E8]'} hover:text-[#C9A458]`
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid={NAV.cta}
            className={`inline-flex items-center gap-3 px-6 py-3 text-[11px] tracking-[0.28em] uppercase border transition-all duration-500 ${
              condensed
                ? 'border-[#18342A] text-[#18342A] hover:bg-[#18342A] hover:text-[#F5F1E8]'
                : 'border-[#C9A458] text-[#F5F1E8] hover:bg-[#C9A458] hover:text-[#18342A]'
            }`}
          >
            Schedule a Visit
            <span className="w-1 h-1 rounded-full bg-current opacity-60" />
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          data-testid={NAV.mobileToggle}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          className={`lg:hidden p-2 -mr-2 transition-colors ${
            condensed ? 'text-[#18342A]' : 'text-[#F5F1E8]'
          }`}
        >
          {open ? <X size={22} strokeWidth={1.4} /> : <Menu size={22} strokeWidth={1.4} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid={NAV.mobileMenu}
          className="lg:hidden bg-[#F5F1E8] border-t border-[#C9A458]/30"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`${l.tid}-mobile`}
                className="text-[#18342A] text-xs tracking-[0.28em] uppercase"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid={`${NAV.cta}-mobile`}
              className="mt-2 inline-block w-fit border border-[#18342A] text-[#18342A] px-6 py-3 text-[11px] tracking-[0.28em] uppercase"
            >
              Schedule a Visit
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
